import type { FileReviewAnchor, FileReviewMessage } from './types';

export const DISCUSSION_PROFILE_PHOTO_URL = '/api/auth/profile/photo';

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when the value is an Entra object id usable with the file-review avatar endpoint. */
export function isDiscussionAuthorGuid(userId: string | null | undefined): boolean {
  return Boolean(userId?.trim() && GUID_PATTERN.test(userId.trim()));
}

/** Same avatar URL as document-editor discussions (`GET /api/file-review/users/{id}/avatar`). */
export function fileReviewUserAvatarUrl(
  userId: string | null | undefined,
  hasAvatar = true
): string | undefined {
  if (!hasAvatar || !isDiscussionAuthorGuid(userId)) return undefined;
  return `/api/file-review/users/${encodeURIComponent(userId!.trim())}/avatar`;
}

export interface FileReviewChatItem {
  id: string;
  type: 'text';
  text: string;
  timestamp: Date;
  author: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  isEdited?: boolean;
  anchorId?: string | null;
  anchorSelectedText?: string | null;
}

function formatAuthorName(message: FileReviewMessage): string {
  return (
    message.authorDisplayName?.trim() ||
    message.submittedByDisplayName?.trim() ||
    message.submittedBy
  );
}

function messageAuthorKeys(message: FileReviewMessage): string[] {
  const keys: string[] = [];
  const userId = message.submittedByUserId?.trim();
  const submittedBy = message.submittedBy?.trim();
  if (userId) keys.push(userId);
  if (submittedBy) keys.push(submittedBy);
  return keys;
}

/** Trim + lowercase for stable author comparisons (matches server OrdinalIgnoreCase). */
export function normalizeUserKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

export function isSameUser(a: string, b: string): boolean {
  const left = normalizeUserKey(a);
  const right = normalizeUserKey(b);
  if (!left || !right || left === 'unknown' || right === 'unknown') return false;
  return left === right;
}

export function isMessageFromCurrentUser(
  message: FileReviewMessage,
  currentUserId: string,
  currentUserAliases: ReadonlySet<string>
): boolean {
  if (currentUserId === 'unknown' && currentUserAliases.size === 0) return false;

  for (const key of messageAuthorKeys(message)) {
    if (isSameUser(key, currentUserId)) return true;
    for (const alias of currentUserAliases) {
      if (isSameUser(key, alias)) return true;
    }
  }
  return false;
}

export function mapReviewMessagesToChatItems(
  messages: FileReviewMessage[],
  currentUserId: string,
  currentUserAliases: Iterable<string>,
  currentUserHasPhoto: boolean | null | undefined
): FileReviewChatItem[] {
  const aliasSet = new Set(currentUserAliases);

  return messages.map((m) => {
    const isCurrentUser = isMessageFromCurrentUser(m, currentUserId, aliasSet);
    const author: FileReviewChatItem['author'] = {
      id: isCurrentUser ? currentUserId : messageAuthorKeys(m)[0] ?? m.submittedBy,
      name: formatAuthorName(m)
    };
    if (isCurrentUser && currentUserHasPhoto !== false) {
      author.avatarUrl = DISCUSSION_PROFILE_PHOTO_URL;
    } else if (m.authorHasAvatar && m.submittedByUserId) {
      author.avatarUrl = fileReviewUserAvatarUrl(m.submittedByUserId, true);
    }
    return {
      id: m.id,
      type: 'text' as const,
      text: m.text,
      timestamp: new Date(m.submittedAt),
      author,
      isEdited: Boolean(m.updatedAt),
      anchorId: m.anchorId,
      anchorSelectedText: m.anchorSelectedText ?? null
    };
  });
}

export function isOwnChatItem(
  message: FileReviewChatItem | undefined,
  messageDto: FileReviewMessage | undefined,
  currentUserId: string,
  currentUserAliases: ReadonlySet<string>
): boolean {
  if (messageDto) {
    return isMessageFromCurrentUser(messageDto, currentUserId, currentUserAliases);
  }
  if (!message?.author?.id || currentUserId === 'unknown') return false;
  if (isSameUser(message.author.id, currentUserId)) return true;
  for (const alias of currentUserAliases) {
    if (isSameUser(message.author.id, alias)) return true;
  }
  return false;
}

export function truncateAnchorQuote(text: string, maxLen = 80): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen)}…`;
}

export function formatAnchorQuote(anchor: FileReviewAnchor): string {
  if (anchor.kind === 'spreadsheet') {
    const name = anchor.sheetName?.trim();
    const text = anchor.selectedText?.trim();
    if (name && text) return `${name} · ${text}`;
    return text || name || 'Cell range';
  }
  return anchor.selectedText?.trim() || 'Selection';
}
